/**
 * The first show.
 *
 * A CONSTANT, not a setting. There is one show this plan was written for —
 * Friday 26 September 2026 — and the fifteen-day checklist exists to get Dillon
 * to it. A date that can be edited invites the deadline being moved instead of
 * the work being done, which is the opposite of what a countdown is for.
 *
 * Later shows are ordinary rows on the Show tab. They do not get a countdown,
 * because by then the business will have done this once and will not need one.
 */
export const SHOW_DATE = '2026-09-26'

/** Day 15 of the plan IS the show. Day N falls (15 - N) days before it. */
export const PLAN_DAYS = 15

/**
 * How long the plan lingers after the show.
 *
 * Three days, then it goes. Not immediately: the closing tasks — reconcile the
 * cash, the ATH receipts, the plants that came home — happen the morning after,
 * and a checklist that vanishes at midnight takes the last four items with it.
 * Not forever either: a permanent countdown to a date in the past is the
 * clearest possible sign that nobody maintains the thing.
 */
export const RETIRE_AFTER_DAYS = 3

/**
 * Parse a plain ISO date as a LOCAL date.
 *
 * `new Date('2026-09-26')` is parsed as UTC midnight, which is the 25th
 * anywhere west of Greenwich — including Vieques, the one place this has to be
 * right. Splitting the parts avoids the whole question.
 */
export function parseDate(iso: string): Date | null {
  const [y, m, d] = iso.split('-').map(Number)
  if (!y || !m || !d) return null
  return new Date(y, m - 1, d)
}

/** Midnight today, in whatever zone the reader is standing in. */
function midnight(at: Date = new Date()): Date {
  const copy = new Date(at)
  copy.setHours(0, 0, 0, 0)
  return copy
}

const DAY = 86_400_000

/** The date of a given plan day. Day 15 is the show. */
export function dateOfPlanDay(day: number, showDate = SHOW_DATE): Date | null {
  const show = parseDate(showDate)
  if (!show) return null
  show.setDate(show.getDate() - (PLAN_DAYS - day))
  return show
}

/**
 * Whole days from today until the show. Negative once it has been.
 */
export function daysUntilShow(showDate = SHOW_DATE, today: Date = new Date()): number | null {
  const show = parseDate(showDate)
  if (!show) return null
  return Math.round((show.getTime() - midnight(today).getTime()) / DAY)
}

/**
 * Has the plan outlived its usefulness?
 *
 * True from the fourth day after the show onward. The plan is hidden rather
 * than deleted: the record of what was done stays in the database, and someone
 * asking "did we test a real ATH payment?" in November can still find out.
 */
export function planRetired(showDate = SHOW_DATE, today: Date = new Date()): boolean {
  const days = daysUntilShow(showDate, today)
  return days !== null && days < -RETIRE_AFTER_DAYS
}

/** What the countdown says. */
export function showtimeHeadline(showDate = SHOW_DATE, today: Date = new Date()): string {
  const days = daysUntilShow(showDate, today)
  if (days === null) return 'Fifteen days to showtime.'
  if (days > 1) return `${days} days to showtime.`
  if (days === 1) return 'Tomorrow is the show.'
  if (days === 0) return 'Today is the show.'
  if (days === -1) return 'The show was yesterday.'
  return 'Closing out the show.'
}
